import { useState, useMemo, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { ChatProvider } from '../context/ChatProvider'
import { ChatWindow } from './ChatWindow'
import {
  requestTherapistToken,
  requestPatientToken,
  createConversation,
  extractTherapistId,
  extractPatientId,
  resolveTwilioConversationSid,
} from '../services/chatApi'
import type {
  ChatConversation,
  PatientInfo,
  TherapistInfo,
} from '@/chat/services/chatApi'
import { UserRole } from '@/types'
import { useAppSelector } from '@/store'
import { useTheme } from '@mui/material/styles'

interface ChatDialogProps {
  open: boolean
  onClose: () => void
  patientId: string | PatientInfo
  therapistId: string | TherapistInfo
  patientDisplayName?: string
  therapistDisplayName?: string
  patientAvatarUrl?: string
  therapistAvatarUrl?: string
}

const getEntityDisplayName = (
  entity: string | { firstName?: string; lastName?: string },
  fallback: string
): string => {
  if (typeof entity === 'string') return fallback
  const first = entity.firstName?.trim() ?? ''
  const last = entity.lastName?.trim() ?? ''
  const combined = `${first} ${last}`.trim()
  return combined || fallback
}

const getEntityAvatarUrl = (
  entity: string | { profilePhotoUrl?: string }
): string | undefined => {
  if (typeof entity === 'string') return undefined
  return entity.profilePhotoUrl
}

const getCurrentUserDisplayName = (user?: {
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  id?: string | null
}): string => {
  if (!user) return 'You'
  const first = user.firstName?.trim() ?? ''
  const last = user.lastName?.trim() ?? ''
  const combined = `${first} ${last}`.trim()
  if (combined) return combined
  if (user.email) return user.email
  if (user.id) return user.id
  return 'You'
}

export const ChatDialog = ({
  open,
  onClose,
  patientId,
  therapistId,
  patientDisplayName,
  therapistDisplayName,
  patientAvatarUrl,
  therapistAvatarUrl,
}: ChatDialogProps) => {
  const { user } = useAppSelector(state => state.auth)
  const [conversation, setConversation] = useState<ChatConversation | null>(
    null
  )
  const [conversationLoading, setConversationLoading] = useState(false)
  const [conversationError, setConversationError] = useState<string | null>(
    null
  )

  const currentUserId = user?.id ?? ''
  const role = user?.role ?? null
  const isTherapist = role === UserRole.THERAPIST

  // Extract IDs
  const therapistIdString = extractTherapistId(therapistId)
  const patientIdString = extractPatientId(patientId)

  // Determine participant info based on current user role
  const participantEntity = isTherapist ? patientId : therapistId

  // Extract display name - prioritize prop, then entity, then fallback
  const participantDisplayName =
    (isTherapist ? patientDisplayName : therapistDisplayName) ??
    getEntityDisplayName(
      participantEntity,
      isTherapist ? 'Patient' : 'Provider'
    )

  // Extract avatar URL - prioritize prop, then entity
  // If entity is an object with profilePhotoUrl, use it even if prop is not provided
  const entityAvatarUrl = getEntityAvatarUrl(participantEntity)
  const participantAvatarUrl =
    (isTherapist ? patientAvatarUrl : therapistAvatarUrl) ?? entityAvatarUrl

  const currentUserDisplayName = getCurrentUserDisplayName(user ?? undefined)

  // Refs to prevent duplicate API calls in Strict Mode
  const isInitializingRef = useRef(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Create conversation when dialog opens
  useEffect(() => {
    if (!open || !currentUserId) {
      setConversation(null)
      setConversationError(null)
      isInitializingRef.current = false
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }
      return
    }

    // Prevent duplicate calls
    if (isInitializingRef.current) {
      return
    }

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    const initializeConversation = async () => {
      isInitializingRef.current = true
      setConversationLoading(true)
      setConversationError(null)

      try {
        const createdConversation = await createConversation(
          {
            therapistId: therapistIdString,
            patientId: patientIdString,
          },
          abortController.signal
        )

        // Check if request was aborted
        if (abortController.signal.aborted) {
          return
        }

        // Create a ChatConversation object compatible with our system
        const chatConversation: ChatConversation = {
          conversationSid: resolveTwilioConversationSid(
            createdConversation.conversationSid,
            createdConversation.conversationId
          ),
          conversationId: createdConversation.conversationId,
          therapistId: therapistId as string | TherapistInfo,
          patientId: patientId as string | PatientInfo,
          reused: createdConversation.reused,
        }

        setConversation(chatConversation)
      } catch (error) {
        // Don't set error if request was aborted
        if (abortController.signal.aborted) {
          return
        }
        console.error('Failed to create conversation:', error)
        setConversationError(
          error instanceof Error
            ? error.message
            : 'Failed to initialize chat. Please try again.'
        )
      } finally {
        if (!abortController.signal.aborted) {
          setConversationLoading(false)
          isInitializingRef.current = false
        }
        abortControllerRef.current = null
      }
    }

    initializeConversation()

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }
      isInitializingRef.current = false
    }
  }, [
    open,
    currentUserId,
    therapistIdString,
    patientIdString,
    therapistId,
    patientId,
  ])

  const tokenFetcher = useMemo(() => {
    if (!currentUserId) return undefined
    return isTherapist
      ? (signal?: AbortSignal) => requestTherapistToken(currentUserId, signal)
      : (signal?: AbortSignal) => requestPatientToken(currentUserId, signal)
  }, [currentUserId, isTherapist])

  const conversationResolver = useMemo(() => {
    if (!conversation || !currentUserId) return undefined

    return async (signal?: AbortSignal): Promise<string> => {
      const sid = resolveTwilioConversationSid(
        conversation.conversationSid,
        conversation.conversationId
      )
      if (!sid) {
        throw new Error('Conversation SID is missing')
      }
      if (!sid.startsWith('CH')) {
        throw new Error(
          `Server returned invalid Twilio conversation SID "${sid}".`
        )
      }
      return sid
    }
  }, [conversation, currentUserId])

  const conversationId = resolveTwilioConversationSid(
    conversation?.conversationSid,
    conversation?.conversationId
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
          borderRadius: 3,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          position: 'relative',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 2,
        }}
      >
        <Typography variant="h6">Send Message</Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          flex: 1,
          p: '0 !important',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {conversationLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Connecting to chat...
            </Typography>
          </Box>
        ) : conversationError ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              gap: 2,
              p: 3,
            }}
          >
            <Typography variant="h6" color="error">
              Unable to start chat
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {conversationError}
            </Typography>
          </Box>
        ) : conversation &&
          tokenFetcher &&
          conversationResolver &&
          conversationId ? (
          <ChatProvider
            key={conversationId}
            config={{
              tokenFetcher,
              conversationResolver,
              enableDebugLogging: import.meta.env.DEV,
              participantDisplayName,
              participantAvatarUrl,
              currentUserDisplayName,
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: '#F6F7F9',
              }}
            >
              <ChatWindow />
            </Box>
          </ChatProvider>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              gap: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Please log in to start messaging
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
